/**
 * Pure mode/position decisions for resilient playback (audio owns the clock
 * while video comes and goes through sparse coverage). Kept side-effect-free
 * so every state transition is exhaustively unit-testable without mocking
 * hls.js or the DOM — `useResilientPlayback.ts` supplies the DOM/hls.js
 * derived inputs (fragment lists, buffered ranges, drift) and applies the
 * outputs computed here.
 */

import { rangesContain, TimeRange } from './availability';

export type ResilientMode = 'both' | 'audioOnly' | 'videoOnly' | 'waiting';

export interface ModeAvailability {
  audioAvailable: boolean;
  videoAvailable: boolean;
}

/**
 * The resilient-mode state table. Driven by whether each medium currently
 * has real (non-gap) published media, never by the coverage endpoint's
 * unions — those are an eventual-consistency poll that can lag the actual
 * playlist, and switching modes on a lagging read would flap the picture.
 */
export function decideMode({ audioAvailable, videoAvailable }: ModeAvailability): ResilientMode {
  if (audioAvailable && videoAvailable) return 'both';
  if (audioAvailable) return 'audioOnly';
  if (videoAvailable) return 'videoOnly';
  return 'waiting';
}

/** A child-playlist fragment's real-world span, gap-flagged from EXT-X-GAP. */
export interface FragmentAvailability {
  startUtcMs: number;
  endUtcMs: number;
  gap: boolean;
}

/**
 * Whether a UTC instant falls inside a real (non-gap) published fragment —
 * the playlist's own ground truth for "is media here", independent of
 * whether the browser has buffered it yet.
 */
export function isPublishedAt(fragments: FragmentAvailability[], utcMs: number): boolean {
  return fragments.some((f) => !f.gap && utcMs >= f.startUtcMs && utcMs < f.endUtcMs);
}

/** Two fragment boundaries this close count as touching, not a real hole —
 *  PROGRAM-DATE-TIME is whole milliseconds but EXTINF can carry
 *  microseconds, so a perfectly contiguous pair of segments can still read
 *  as `end !== nextStart` by a sub-millisecond rounding remainder. */
const FRAGMENT_CONTIGUITY_TOLERANCE_MS = 1;

function fragmentsAreContiguous(endUtcMs: number, nextStartUtcMs: number): boolean {
  return Math.abs(endUtcMs - nextStartUtcMs) <= FRAGMENT_CONTIGUITY_TOLERANCE_MS;
}

/**
 * The latest CONTIGUOUS run of published (non-gap) fragments, as one UTC-ms
 * span — the live-edge placement target's region. The backend routinely
 * marks unclaimed live cells as gaps after a grace period, so a gap at the
 * very tail must not hide an otherwise-healthy published run just behind
 * it: this first skips trailing gaps to find the newest PUBLISHED fragment,
 * then walks backward from there while fragments stay gap-free and
 * time-contiguous with their successor. `null` only when every fragment is
 * a gap — nothing published at all. Assumes `fragments` is in playlist
 * order (oldest first), same as hls.js's own list.
 */
export function latestContiguousPublishedRange(fragments: FragmentAvailability[]): TimeRange | null {
  let i = fragments.length - 1;
  while (i >= 0 && fragments[i].gap) i -= 1;
  if (i < 0) return null;

  let end = fragments[i].endUtcMs;
  let start = fragments[i].startUtcMs;
  i -= 1;
  while (i >= 0) {
    const f = fragments[i];
    if (f.gap || !fragmentsAreContiguous(f.endUtcMs, start)) break; // a gap or a real hole — the run stops here
    start = f.startUtcMs;
    i -= 1;
  }
  return [start, end];
}

/** Buffered spans converted to UTC ms pairs use the same containment check as availability bands. */
export function isTimeBuffered(bufferedRangesUtc: TimeRange[], utcMs: number): boolean {
  return rangesContain(bufferedRangesUtc, utcMs);
}

export interface MountedRevision {
  masterUrl: string;
  /** Coverage's `coverage_life`: a purge-and-recreate keeps the master URL but is a new recording. */
  life: string | null;
  rev: number | null;
}

/**
 * Floors a newly observed snapshot_rev at the one already mounted for this
 * masterUrl and session life. Two coverage polls can resolve out of order and
 * hand the caller a lower rev than one already applied; reloading on that
 * decrease would tear down and rebuild both hls.js instances for a stale read.
 * A different masterUrl is a new session and always takes its own rev outright,
 * and so does a different `life`: a purge-and-recreate keeps the URL but starts
 * its revisions over, so flooring across it would pin the player to the
 * recording that no longer exists until the new one outgrew the old maximum.
 */
export function monotonicMountRev(
  mounted: MountedRevision | null,
  masterUrl: string,
  life: string | null,
  rev: number | null,
): number | null {
  if (!mounted || mounted.masterUrl !== masterUrl || mounted.life !== life || mounted.rev === null) {
    return rev;
  }
  if (rev === null || rev < mounted.rev) return mounted.rev;
  return rev;
}

/** Below this, leave video alone — hls.js's own segment-boundary jitter is well under it. */
/** hls.js recovers a media error by detaching and reattaching the element, so an
 *  unbounded loop re-initialises playback forever and never reports failure. */
export const MAX_MEDIA_RECOVERIES = 3;
/** A quiet spell this long means the next media error starts a fresh budget. */
export const MEDIA_RECOVERY_RESET_MS = 60_000;

export const SYNC_TOLERANCE_MS = 250;
/** Above this, the two clocks have visibly diverged and are worth a corrective seek. */
export const SYNC_RESYNC_THRESHOLD_MS = 500;

export function isWithinSyncTolerance(driftMs: number): boolean {
  return Math.abs(driftMs) <= SYNC_TOLERANCE_MS;
}

/**
 * Only resync toward media the playlist actually publishes — seeking into an
 * unpublished gap can never converge. Buffered-ness is deliberately NOT a
 * condition here: a published-but-not-yet-buffered target still needs a real
 * seek so the player starts loading it (buffered-ness instead gates whether
 * to keep the picture visible — see the controller's hysteresis).
 */
export function needsResync(driftMs: number, targetPublished: boolean): boolean {
  return targetPublished && Math.abs(driftMs) > SYNC_RESYNC_THRESHOLD_MS;
}

/**
 * Build-time default from `VITE_RESILIENT_PLAYBACK`. The runtime
 * `?resilient=1` / `?resilient=0` query override always wins, in either
 * direction, so one staging session can be flipped without a rebuild.
 */
export function resolveResilientFlag(buildFlagOn: boolean, search: string): boolean {
  const override = new URLSearchParams(search).get('resilient');
  if (override === '1') return true;
  if (override === '0') return false;
  return buildFlagOn;
}

export interface MasterManifestUris {
  audioUri: string | null;
  videoUri: string | null;
}

/**
 * Extract the AUDIO EXT-X-MEDIA URI and the (single) variant's URI from the
 * v2 master this player already loaded. Never derive these URLs any other
 * way — the authorized master already carries the right share scope and
 * finalized revision, and reconstructing a child URL from parts risks
 * dropping either.
 */
export function parseMasterManifest(text: string): MasterManifestUris {
  const lines = text.split(/\r?\n/);
  let audioUri: string | null = null;
  let videoUri: string | null = null;
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (line.startsWith('#EXT-X-MEDIA:') && /TYPE=AUDIO/.test(line)) {
      const match = line.match(/URI="([^"]*)"/);
      if (match) audioUri = match[1];
      continue;
    }
    if (line.startsWith('#EXT-X-STREAM-INF:')) {
      for (let j = i + 1; j < lines.length; j += 1) {
        const candidate = lines[j].trim();
        if (candidate === '' || candidate.startsWith('#')) continue;
        videoUri = candidate;
        break;
      }
    }
  }
  return { audioUri, videoUri };
}

/**
 * Resolve a child URI the way a browser resolves a relative URL against the
 * document that referenced it — the only way to keep a `/share/{token}/...`
 * path's scope and a child's own `?fin=N` query intact instead of
 * reconstructing a URL that silently drops either.
 */
export function resolveChildPlaylistUrl(masterUrl: string, childUri: string): string {
  const base = new URL(masterUrl, window.location.origin);
  return new URL(childUri, base).toString();
}
